import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const adminCacheRef = React.useRef<{ userId: string; isAdmin: boolean } | null>(null);

  const checkAdminRole = async (userId: string) => {
    // Use cached result if same user
    if (adminCacheRef.current?.userId === userId) {
      return adminCacheRef.current.isAdmin;
    }
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("Error checking admin role:", error);
        return false;
      }
      const result = !!data;
      adminCacheRef.current = { userId, isAdmin: result };
      return result;
    } catch (err) {
      console.error("Error checking admin role:", err);
      return false;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Fire admin check in background - don't block auth resolution
          checkAdminRole(session.user.id).then(setIsAdmin);
          // On sign-in, claim any pending listings submitted with this email
          if (event === 'SIGNED_IN' && session.user.email) {
            supabase.rpc('claim_pending_listings_for_user', {
              p_user_id: session.user.id,
              p_email: session.user.email,
            }).then(({ data, error }) => {
              if (error) console.warn('Pending listing claim failed:', error.message);
              else if (data && data > 0) console.log(`Claimed ${data} pending listing(s)`);
            });
          }
        } else {
          setIsAdmin(false);
          adminCacheRef.current = null;
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Fire admin check in background - don't block auth resolution
        checkAdminRole(session.user.id).then(setIsAdmin);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/dashboard?newuser=true`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        }
      }
    });

    // Send branded welcome email (fire-and-forget; don't block signup on email errors)
    if (!error && data?.user) {
      supabase.functions
        .invoke('send-welcome-email', {
          body: { recipientName: fullName },
        })
        .catch((err) => console.warn('Welcome email failed to enqueue:', err));
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };


  const resetPassword = async (email: string) => {
    try {
      const response = await supabase.functions.invoke('send-password-reset', {
        body: {
          email,
          redirectUrl: `${window.location.origin}/reset-password`,
        },
      });

      if (response.error) {
        return { error: new Error(response.error.message || 'Failed to send reset email') };
      }

      return { error: null };
    } catch (error: any) {
      return { error: new Error(error.message || 'Failed to send reset email') };
    }
  };

  const signOut = async () => {
    setIsAdmin(false);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signUp, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};
